import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useAuth } from '../context/AuthContext';
import useAuthRequired from '../hooks/useAuthRequired';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const Billing = () => {
  useAuthRequired();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('records'); // 'records' or 'invoices'
  const [billingRecords, setBillingRecords] = useState([]);
  const [invoiceRequests, setInvoiceRequests] = useState([]);
  const [billingStats, setBillingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [selectedBillingRecord, setSelectedBillingRecord] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [cancelRequestId, setCancelRequestId] = useState(null);

  // Invoice form state
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_type: 'personal',
    invoice_title: '',
    tax_id: '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  });

  useEffect(() => {
    loadBillingData();
    markInvoicesAsViewed();
  }, []);

  // Mark completed invoices as viewed when user enters billing page
  const markInvoicesAsViewed = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/billing/invoices/mark-viewed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Refresh user data to update notification badge
      refreshUser();
    } catch (error) {
      // Silent fail - this is not critical
      console.log('Mark invoices as viewed (non-critical):', error.message);
    }
  };

  const loadBillingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Load billing records
      const recordsResponse = await axios.get(`${API_URL}/billing/records`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingRecords(recordsResponse.data.data.records || []);

      // Load invoice requests
      const invoicesResponse = await axios.get(`${API_URL}/billing/invoices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvoiceRequests(invoicesResponse.data.data.requests || []);

      // Load billing stats
      const statsResponse = await axios.get(`${API_URL}/billing/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBillingStats(statsResponse.data.data);

    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRequestInvoice = (record) => {
    setSelectedBillingRecord(record);
    setInvoiceForm({
      ...invoiceForm,
      email: user?.email || '',
      invoice_title: user?.full_name || ''
    });
    setShowInvoiceForm(true);
  };

  const handleSubmitInvoiceRequest = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/billing/invoices`,
        {
          ...invoiceForm,
          billing_record_id: selectedBillingRecord.id
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setSuccessMessage('Invoice request submitted successfully!');
      setShowInvoiceForm(false);
      setActiveTab('invoices'); // Switch to invoices tab to show the new request
      loadBillingData();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Failed to submit invoice request:', error);
      setErrorMessage(error.response?.data?.error?.message || 'Failed to submit invoice request');

      // Clear error message after 5 seconds
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleCancelInvoiceRequest = async (requestId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/billing/invoices/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage('Invoice request cancelled successfully');
      setCancelRequestId(null);
      loadBillingData();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Failed to cancel invoice request:', error);
      setErrorMessage('Failed to cancel invoice request');
      setCancelRequestId(null);

      // Clear error message after 5 seconds
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusClasses = {
      completed: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled_by_user: 'bg-gray-100 text-gray-800'
    };
    return statusClasses[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusDisplayName = (status) => {
    const statusNames = {
      completed: 'completed',
      pending: 'pending',
      processing: 'processing',
      failed: 'failed',
      refunded: 'refunded',
      rejected: 'rejected',
      cancelled_by_user: 'Cancelled by User'
    };
    return statusNames[status] || status;
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex">
        <Sidebar />
        <div className="flex-1 flex flex-col">
          <Header />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading billing information...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Header />
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header with Back Button */}
            <div className="mb-8">
              <div className="flex items-center mb-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  Back to Dashboard
                </button>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">Billing</h1>
              <p className="mt-2 text-gray-600">Manage your payment records and invoice requests</p>
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-green-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-green-800 font-medium">{successMessage}</p>
                </div>
                <button
                  onClick={() => setSuccessMessage('')}
                  className="text-green-600 hover:text-green-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* Error Message */}
            {errorMessage && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-red-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-red-800 font-medium">{errorMessage}</p>
                </div>
                <button
                  onClick={() => setErrorMessage('')}
                  className="text-red-600 hover:text-red-800"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

                {/* Statistics Cards */}
                {billingStats && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Spent</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {formatCurrency(billingStats.total_spent)}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Payments</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {billingStats.total_payments}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Pending Invoices</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {billingStats.pending_invoices}
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="border-b border-gray-200">
                <nav className="flex -mb-px">
                  <button
                    onClick={() => setActiveTab('records')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === 'records'
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Payment Records
                  </button>
                  <button
                    onClick={() => setActiveTab('invoices')}
                    className={`px-6 py-4 text-sm font-medium border-b-2 ${
                      activeTab === 'invoices'
                        ? 'border-primary-600 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Invoice Requests
                  </button>
                </nav>
              </div>

              <div className="p-6">
                {/* Payment Records Tab */}
                {activeTab === 'records' && (
                  <div>
                    {billingRecords.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No payment records</h3>
                        <p className="mt-1 text-sm text-gray-500">You haven't made any payments yet.</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Date
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Amount
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Method
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {billingRecords.map((record) => (
                              <tr key={record.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                  {formatDate(record.payment_date || record.created_at)}
                                </td>
                                <td className="px-6 py-4 text-sm text-gray-900">
                                  {record.description || `${record.plan_name || 'Unknown'} Plan - ${record.billing_period || 'monthly'}`}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                  {formatCurrency(record.amount, record.currency)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                                  {record.payment_method}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(record.payment_status)}`}>
                                    {record.payment_status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  {record.payment_status === 'completed' && !record.invoice_request_id && (
                                    <button
                                      onClick={() => handleRequestInvoice(record)}
                                      className="text-primary-600 hover:text-primary-700 font-medium"
                                    >
                                      Request Invoice
                                    </button>
                                  )}
                                  {record.invoice_request_id && (
                                    <span className="text-gray-500 text-sm">
                                      Invoice {record.invoice_request_status}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* Invoice Requests Tab */}
                {activeTab === 'invoices' && (
                  <div>
                    {invoiceRequests.length === 0 ? (
                      <div className="text-center py-12">
                        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No invoice requests</h3>
                        <p className="mt-1 text-sm text-gray-500">You haven't requested any invoices yet.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {invoiceRequests.map((request) => (
                          <div key={request.id} className="border border-gray-200 rounded-lg p-6">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <h3 className="text-lg font-medium text-gray-900">
                                    {request.invoice_title}
                                  </h3>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(request.status)}`}>
                                    {getStatusDisplayName(request.status)}
                                  </span>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <p className="text-gray-500">Invoice Type</p>
                                    <p className="text-gray-900 capitalize">{request.invoice_type}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Amount</p>
                                    <p className="text-gray-900 font-medium">
                                      {formatCurrency(request.amount, request.currency)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Email</p>
                                    <p className="text-gray-900">{request.email}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-500">Request Date</p>
                                    <p className="text-gray-900">{formatDate(request.created_at)}</p>
                                  </div>
                                  {request.tax_id && (
                                    <div>
                                      <p className="text-gray-500">Tax ID</p>
                                      <p className="text-gray-900">{request.tax_id}</p>
                                    </div>
                                  )}
                                  {request.invoice_number && (
                                    <div>
                                      <p className="text-gray-500">Invoice Number</p>
                                      <p className="text-gray-900">{request.invoice_number}</p>
                                    </div>
                                  )}
                                </div>
                                {request.invoice_file_url && (
                                  <div className="mt-4">
                                    <a
                                      href={`${new URL(API_URL).origin}${request.invoice_file_url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center text-primary-600 hover:text-primary-700 font-medium"
                                    >
                                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                      Download Invoice
                                    </a>
                                  </div>
                                )}
                              </div>
                              <div>
                                {(request.status === 'pending' || request.status === 'processing') && (
                                  cancelRequestId === request.id ? (
                                    <div className="flex items-center space-x-2">
                                      <span className="text-sm text-gray-700 mr-1">Cancel?</span>
                                      <button
                                        onClick={() => handleCancelInvoiceRequest(request.id)}
                                        className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded hover:bg-red-700 transition"
                                      >
                                        Yes
                                      </button>
                                      <button
                                        onClick={() => setCancelRequestId(null)}
                                        className="px-3 py-1 bg-gray-200 text-gray-700 text-sm font-medium rounded hover:bg-gray-300 transition"
                                      >
                                        No
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => setCancelRequestId(request.id)}
                                      className="text-red-600 hover:text-red-700 text-sm font-medium"
                                    >
                                      Cancel
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>

      {/* Invoice Request Form Modal */}
      {showInvoiceForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Request Invoice</h2>
              <p className="mt-1 text-sm text-gray-600">
                Fill in the details for your invoice request
              </p>
            </div>

            <form onSubmit={handleSubmitInvoiceRequest} className="p-6 space-y-6">
              {/* Invoice Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="personal"
                      checked={invoiceForm.invoice_type === 'personal'}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_type: e.target.value })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Personal</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="company"
                      checked={invoiceForm.invoice_type === 'company'}
                      onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_type: e.target.value })}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">Company</span>
                  </label>
                </div>
              </div>

              {/* Invoice Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {invoiceForm.invoice_type === 'company' ? 'Company Name' : 'Full Name'} *
                </label>
                <input
                  type="text"
                  required
                  value={invoiceForm.invoice_title}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Tax ID (for company) */}
              {invoiceForm.invoice_type === 'company' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tax ID / Business Registration Number
                  </label>
                  <input
                    type="text"
                    value={invoiceForm.tax_id}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, tax_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={invoiceForm.email}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={invoiceForm.phone}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, phone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={invoiceForm.address}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* City, State, Postal Code */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={invoiceForm.city}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, city: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province
                  </label>
                  <input
                    type="text"
                    value={invoiceForm.state}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, state: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={invoiceForm.postal_code}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, postal_code: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Country
                </label>
                <input
                  type="text"
                  value={invoiceForm.country}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, country: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              {/* Buttons */}
              <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowInvoiceForm(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Billing;
